
import { Component, Vue, Prop } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import ToggleSwitch from "@/components/elements/ToggleSwitch.vue";
import { InstallationData } from "../../../../shared/github";
import { Repo, ReviewSyncSettings } from "../../../../shared/types";
import InboxModule from "../../store/modules/inbox";
import {
  getReviewSyncSettingsLabels,
  hasPermission,
  STATE_SETTING_KEYS
} from "../../../../shared/typeUtils";
@Component({
  components: {
    ToggleSwitch
  }
})
export default class SettingsInstallationItem extends Vue {
  @Prop() public i!: InstallationData;
  public repos: Array<{
    repo: InstallationData["repositories"][number];
    auto_review: boolean;
    default_resolve_threads: boolean;
    review_sync_settings: ReviewSyncSettings;
  }> = [];
  public expanded = false;

  private inboxModule = getModule(InboxModule, this.$store);

  get reviewSyncSettingsKeys() {
    return STATE_SETTING_KEYS;
  }

  async mounted() {
    this.repos = this.i.repositories.map(repo => {
      const reviewSyncSettings = this.inboxModule.repoReviewSyncSettings(
        this.i.account.login,
        repo.name
      );
      return {
        repo,
        auto_review: this.inboxModule.repoAutoReviewEnabled(
          this.i.account.login,
          repo.name
        ),
        default_resolve_threads: this.inboxModule.repoDefaultResolveThreads(
          this.i.account.login,
          repo.name
        ),
        review_sync_settings: {
          sync_github_comments:
            reviewSyncSettings.sync_github_comments ?? false,
          post_native_inline_comments:
            reviewSyncSettings.post_native_inline_comments ?? false,
          disable_auto_assignees:
            reviewSyncSettings.disable_auto_assignees ?? false
        }
      };
    });
  }
  get orgAutoReview() {
    return this.inboxModule.orgAutoReviewEnabled(this.i.account.login);
  }
  public onOrgToggleAutoReview(val: boolean) {
    this.inboxModule.toggleAutoReview({ org: this.i.account.login, val });
  }

  public onRepoToggleAutoReview(repo: string, val: boolean) {
    this.inboxModule.toggleAutoReview({ org: this.i.account.login, repo, val });
  }

  public onRepoToggleResolveThreadsByDefault(repo: string, val: boolean) {
    this.inboxModule.toggleResolveThreadsByDefault({
      org: this.i.account.login,
      repo,
      val
    });
  }

  public getRepoSettingValue(repo: Partial<Repo>, key: string): boolean {
    return repo.review_sync_settings?.[key] ?? false;
  }

  public onToggleReviewSyncSetting(
    repoName: string,
    key: string,
    val: boolean
  ) {
    const r = this.repos.find(x => x.repo.name === repoName);
    if (r) {
      r.review_sync_settings[key] = val;
      this.inboxModule.updateReviewSyncSettings({
        org: this.i.account.login,
        repo: repoName,
        settings: r.review_sync_settings
      });
    }
  }

  public getLabelForKey(key: keyof ReviewSyncSettings): string {
    return getReviewSyncSettingsLabels(key).title;
  }

  public getDescriptionForKey(key: keyof ReviewSyncSettings): string {
    return getReviewSyncSettingsLabels(key).description;
  }

  /**
   * Whether the toggle for a given setting should be disabled because the
   * installation is missing required GitHub App permissions.
   */
  public isSettingDisabled(key: keyof ReviewSyncSettings): boolean {
    const perms = this.inboxModule.orgAppPermissions(this.i.account.login);
    if (key === "post_native_inline_comments") {
      return (
        !hasPermission(perms, "contents", "write") ||
        !hasPermission(perms, "pull_requests", "write")
      );
    }
    return false;
  }

  public async onRefreshPermissions() {
    await this.inboxModule.refreshOrgPermissions(this.i.account.login);
  }
}
